import {Component, Inject, OnDestroy, OnInit, PLATFORM_ID} from '@angular/core';
import {ActivatedRouteSnapshot, NavigationEnd, Router, RouterOutlet} from '@angular/router';
import {ToastComponent} from './toast/toast.component';
import {Intercom} from '@intercom/messenger-js-sdk';
import {environment} from '../environments/environment';
import {filter, map, Subject, takeUntil} from 'rxjs';
import {ToastService} from './toast/toast.service';
import {isPlatformBrowser, NgClass, NgIf} from '@angular/common';
import {HeaderComponent} from './header/header.component';
import {FooterComponent} from './footer/footer.component';
import {SeoService} from '../services/seo.service';
import {StorageService} from '../services/storage-service';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, ToastComponent, NgIf, HeaderComponent, FooterComponent, NgClass],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit, OnDestroy {
  onDestroy$ = new Subject<boolean>();

  // Navigating through different routes, we want to hide some elements before the view is loaded (ex. footer)
  isHideHeader = false;
  isHideFooter = true; // Lazy initialization
  isHideChat = true;   // Lazy initialization
  isHideCrawler = false;

  constructor(@Inject(PLATFORM_ID) private platformId: Object,
              private router: Router,
              private storageService: StorageService,
              private toastService: ToastService,
              private seoService: SeoService) {
  }

  ngOnInit() {
    // Refresh client version
    this.refreshClientVersion()

    // Route change handler (end)
    this.router.events.pipe(
      takeUntil(this.onDestroy$),
      filter(event => event instanceof NavigationEnd),
      map(() => {
        let currentRoute = this.router.routerState.snapshot.root;

        // Hack to obtain the route data
        while (currentRoute.firstChild) currentRoute = currentRoute.firstChild;
        return currentRoute;
      }))
      .subscribe((route: ActivatedRouteSnapshot) => {

        // Clear any open queued toasts
        this.toastService.clear();

        // Configure hiding global components
        this.isHideHeader = route.data['hideHeader'] || false;
        this.isHideFooter = route.data['hideFooter'] || false;
        this.isHideChat = route.data['hideChat'] || false;
        this.isHideCrawler = route.data['hideCrawler'] || false;

        // SEO
        this.seoService.setRobots(this.isHideCrawler ? 'none' : 'all')
        this.seoService.setTitle(route.data['title'])
        this.seoService.setDescription(route.data['description'])
        this.seoService.setImage(route.data['image'])
        this.seoService.setUrl(route.data['url'])

        // Initialize intercom
        Intercom({
          api_base: 'https://api-iam.intercom.io',
          app_id: environment.INTERCOM_APP_ID,
          hide_default_launcher: this.isHideChat
        })
      })
  }

  ngOnDestroy() {
    this.onDestroy$.next(true);
    this.onDestroy$.complete();
  }

  refreshClientVersion() {
    if (isPlatformBrowser(this.platformId)) {
      const latestVersion = environment.CLIENT_VERSION
      const currentVersion = this.storageService.get(StorageService.CLIENT_VERSION)

      if (currentVersion !== latestVersion) {
        this.storageService.clear()
        this.storageService.add(StorageService.CLIENT_VERSION, latestVersion)
      }
    }
  }
}
